import { press, animateView } from 'https://cdn.jsdelivr.net/npm/motion-dom@12.38.0/+esm';
import { spring } from 'https://cdn.jsdelivr.net/npm/motion@12.38.0/+esm';
import { $4, fetchConfig } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';

const { cartUpdate, cartError, loadingStart, loadingEnd, cartLive, cartTab } = ThemeEvents;

class SnBuildYourOwn extends HTMLElement {
  #controller;
  #variantAvailable = true;
  #isPreOrder = false;
  #isSubmitting = false;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.form = $4('form', this);
    this.submitButton = $4('[type="submit"]', this);
    this.submitText = $4('[data-byo-submit-text]', this);
    this.mainVariantInput = $4('[data-byo-main-variant]', this);
    this.errorWrapper = $4('.hdt-form__message-wrapper', this);
    this.errorMessage = $4('.hdt-error-message', this.errorWrapper);
    this.bundleItems = Array.from(this.querySelectorAll('[data-byo-item]')).map((el) => ({
      el,
      checkbox: $4('[data-byo-checkbox]', el),
      toggle: $4('[data-byo-toggle]', el),
      variantInput: $4('[data-byo-variant-id]', el),
    }));
    this.cart = $4('[ref="hdt-cart"]');

    this.form?.addEventListener('submit', this.#onSubmit, { signal });
    this.bundleItems.forEach(({ checkbox, toggle, el }) => {
      checkbox?.addEventListener('change', this.#onSelectionChange, { signal });
      this.#syncToggleState({ checkbox, toggle, el });

      if (!toggle || !checkbox) return;

      press(toggle, (element) => {
        if (checkbox.disabled) return;

        animateView(
          () => {
            checkbox.checked = !checkbox.checked;
            element.classList.toggle('on', checkbox.checked);
            checkbox.dispatchEvent(new Event('change', { bubbles: true }));
          },
          { type: spring, bounce: 0.3, visualDuration: 0.3 }
        );
      });
    });
    this.querySelectorAll('[data-byo-view-details-trigger]').forEach((trigger) => {
      trigger.addEventListener('click', this.#onViewDetailsClick, { signal });
    });

    const productFormId = this.dataset.productForm;
    if (productFormId) {
      document.forms[productFormId]?.addEventListener('product:updateNow', this.#onVariantUpdate, { signal });
    }

    this.#variantAvailable = !this.submitButton?.hasAttribute('disabled');
    this.#isPreOrder = this.dataset.preOrder === 'true';
    this.#initDetailsHeights();
    this.#updateSubmitState();
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  get minSelections() {
    const value = parseInt(this.dataset.minSelection, 10);
    return Number.isFinite(value) && value > 0 ? value : 3;
  }

  get maxSelections() {
    const value = parseInt(this.dataset.maxSelection, 10);
    return Number.isFinite(value) && value > 0 ? value : 3;
  }

  get selectedCount() {
    return this.bundleItems.filter(({ checkbox, el }) => {
      return checkbox?.checked && !el.classList.contains('is-unavailable');
    }).length;
  }

  #initDetailsHeights() {
    this.querySelectorAll('[data-byo-details]').forEach((detailsEl) => {
      const inner = $4('.sn-byo__product-details-inner', detailsEl);
      if (!inner) return;
      detailsEl.dataset.height = String(inner.scrollHeight + 24);
      detailsEl.style.maxHeight = '0';
    });
  }

  #onVariantUpdate = (event) => {
    const { variant } = event.detail;
    if (!variant || !this.mainVariantInput) return;

    this.mainVariantInput.value = variant.id;
    this.#variantAvailable = variant.available;
    this.#isPreOrder = Boolean(variant.isPreOrder);

    this.#updateSubmitState();
  };

  #syncToggleState({ checkbox, toggle, el }) {
    if (!checkbox || !toggle) return;
    toggle.classList.toggle('on', checkbox.checked);
    toggle.setAttribute('aria-pressed', String(checkbox.checked));
    toggle.toggleAttribute('disabled', checkbox.disabled);
    el?.classList.toggle('is--checked', checkbox.checked);
  }

  #onSelectionChange = (event) => {
    const checkbox = event.currentTarget;
    const item = checkbox.closest('[data-byo-item]');
    const toggle = $4('[data-byo-toggle]', item);
    this.#syncToggleState({ checkbox, toggle, el: item });

    if (this.selectedCount >= this.maxSelections) {
      this.#disableUnselectedItems();
    } else {
      this.#enableSelectableItems();
    }

    this.#updateSubmitState();
  };

  #disableUnselectedItems() {
    this.bundleItems.forEach(({ el, checkbox, toggle }) => {
      if (el.classList.contains('is-unavailable') || checkbox?.checked) return;
      checkbox.disabled = true;
      toggle?.setAttribute('disabled', '');
      el.classList.add('is-disabled');
    });
  }

  #enableSelectableItems() {
    this.bundleItems.forEach(({ el, checkbox, toggle }) => {
      if (el.classList.contains('is-unavailable')) return;
      checkbox.disabled = false;
      toggle?.removeAttribute('disabled');
      el.classList.remove('is-disabled');
    });
  }

  #getSubmitText() {
    const { preOrder, addToCart, soldOut } = themeHDN.strings;

    if (!this.#variantAvailable) return soldOut;
    if (this.selectedCount >= this.minSelections) return addToCart;
    if (this.#isPreOrder) return preOrder;

    return this.submitText?.dataset.byoTextDefault || '';
  }

  #updateSubmitState() {
    if (!this.submitButton) return;
    this.submitButton.toggleAttribute(
      'disabled',
      !this.#variantAvailable || this.selectedCount < this.minSelections
    );

    if (this.submitText) {
      this.submitText.textContent = this.#getSubmitText();
    }
  }

  #buildCartItems() {
    const mainVariantId = String(this.mainVariantInput?.value || '');
    const bundleId = `${mainVariantId}-${Date.now()}`;
    let childIds = '';

    for (const { el, checkbox, variantInput } of this.bundleItems) {
      if (!checkbox?.checked) continue;

      const variantId = String(variantInput?.value || '');
      const productId = String(el.dataset.productId || '');
      if (!variantId || variantId === mainVariantId) continue;

      childIds = childIds ? `${childIds},${productId}-${variantId}` : `${productId}-${variantId}`;
    }

    return [
      {
        id: parseInt(mainVariantId, 10),
        quantity: 1,
        properties: {
          '_Child ID': childIds ? `${childIds}, parent-${mainVariantId}` : `parent-${mainVariantId}`,
          '_unique_bundle_id': bundleId,
        },
      },
    ];
  }

  #handleErrorMessage(message = false) {
    if (!this.errorWrapper) return;
    this.errorWrapper.toggleAttribute('hidden', !message);
    if (message && this.errorMessage) this.errorMessage.textContent = message;
  }

  #parseCartFromSections(sections) {
    const cartJson = sections?.['cart-json'];
    if (!cartJson) return {};

    const payload = cartJson.split('[split_94]')[1];
    if (!payload) return {};

    try {
      return JSON.parse(payload);
    } catch {
      return {};
    }
  }

  #dispatchCartCount(cartData) {
    const { item_count: itemCount, items } = cartData;
    if (itemCount == null) return;

    const detail = { item_count: itemCount, items };
    document.dispatchEvent(new CustomEvent(cartLive, { detail }));
    document.dispatchEvent(new CustomEvent(cartTab, { detail }));
  }

  #onSubmit = async (event) => {
    event.preventDefault();

    if (this.#isSubmitting || this.selectedCount < this.minSelections) return;

    this.#isSubmitting = true;
    const items = this.#buildCartItems();
    this.submitButton?.setAttribute('disabled', '');
    this.submitButton?.setAttribute('aria-disabled', 'true');
    this.submitButton?.setAttribute('aria-busy', 'true');
    this.#handleErrorMessage();
    document.dispatchEvent(new CustomEvent(loadingStart));

    try {
      const sectionIds = [
        ...new Set([
          ...(this.cart?.sectionsToRender?.map((section) => section.id) ?? []),
          'cart-json',
        ]),
      ];
      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        ...fetchConfig('json', {
          body: JSON.stringify({ items, sections: sectionIds }),
        }),
      });
      const responseJson = await response.json();

      if (responseJson.status) {
        const message = responseJson.description || responseJson.message;
        this.#handleErrorMessage(message);
        this.form?.dispatchEvent(
          new CustomEvent(cartError, {
            bubbles: true,
            detail: {
              source: 'sn-build-your-own',
              errors: responseJson.errors || message,
              message,
            },
          })
        );
        return;
      }

      if (!responseJson.sections?.['cart-json']) {
        document.dispatchEvent(new CustomEvent(ThemeEvents.cartReload, { bubbles: true }));
        return;
      }

      let cartData = Object.assign({}, responseJson, this.#parseCartFromSections(responseJson.sections));

      if (cartData.item_count == null) {
        const freshCart = await fetch(`${Shopify.routes.root}cart.js`).then((res) => res.json());
        cartData = { ...cartData, item_count: freshCart.item_count, items: freshCart.items };
      }

      this.#dispatchCartCount(cartData);

      this.form?.dispatchEvent(
        new CustomEvent(cartUpdate, {
          bubbles: true,
          detail: {
            source: 'product-form',
            variantId: String(this.mainVariantInput?.value || ''),
            cartData,
          },
        })
      );
    } catch (error) {
      console.error('Build your own add to cart failed:', error);
    } finally {
      this.#isSubmitting = false;
      this.submitButton?.removeAttribute('aria-disabled');
      this.submitButton?.removeAttribute('aria-busy');
      this.#updateSubmitState();
      document.dispatchEvent(new CustomEvent(loadingEnd));
    }
  };

  #onViewDetailsClick = (event) => {
    if (event.target.closest('a, [data-byo-toggle], .sn-byo__toggle-wrap')) return;

    const item = event.currentTarget.closest('[data-byo-item]');
    const viewButton = $4('[data-byo-view-details]', item);
    const detailsEl = $4('[data-byo-details]', item);
    if (!detailsEl) return;

    const isOpen = item.classList.toggle('is-expanded');
    viewButton?.classList.toggle('is-open', isOpen);
    detailsEl.style.maxHeight = isOpen ? `${detailsEl.dataset.height || 0}px` : '0';
  };
}

customElements.define('sn-build-your-own', SnBuildYourOwn);
